import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TutorialPricingSection from "@/components/TutorialPricingSection";
import { Send, Download, Settings, Key, AlertCircle, Shield, Play } from "lucide-react";
import { Helmet } from "react-helmet-async";
import tutorialImage from "@/assets/installation-iptv-android.jpg";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const IptvAndroid = () => {
  useAffiliateTracking();
  const alternativeLinks = [
    "http://az.dmsmarters2.com",
    "http://dm.pixehd.me:2082",
    "http://dm.opixtv.com:2082",
    "http://dreamsmart.me:2082",
    "http://chrf.25atg09.6g.in"
  ];

  return (
    <>
      <Helmet>
        <title>Tutoriel IPTV Android - Installation Smarters Pro | RoiDuStreaming</title>
        <meta name="description" content="Guide complet pour installer et configurer l'IPTV sur Android avec IPTV Smarters Pro. Tutoriel étape par étape." />
        <meta name="keywords" content="IPTV Android, Smarters Pro, tutoriel IPTV Android, installer IPTV téléphone" />
        <link rel="canonical" href="https://roidustreaming.com/tutoriels/iptv-android" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[50vh] flex items-center justify-center px-4 py-20 hero-bg">
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <span className="text-7xl mb-6 block">📱</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
              Tutoriel IPTV sur Android
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Installation avec IPTV Smarters Pro
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Image Android */}
            <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={tutorialImage} 
                alt="Téléphone Android pour tutoriel IPTV" 
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Étape 1 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">1️⃣ Activer les autorisations nécessaires</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Ouvre les <strong>Paramètres</strong> de ton téléphone.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Descends tout en bas et appuie sur « <strong>À propos du téléphone</strong> ».</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Appuie <strong>5 fois</strong> sur « Numéro de build » pour activer le mode développeur.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Retourne dans <strong>Paramètres &gt; Sécurité</strong> et active « Autoriser les sources inconnues ».</span>
                </li>
              </ul>
            </div>

            {/* Étape 2 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">2️⃣ Télécharger et installer IPTV Smarters Pro</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Depuis ton navigateur, recherche « <strong>IPTV Smarters Pro APK</strong> ».</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Télécharge l'application.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Installe-la en confirmant les autorisations demandées.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">👉</span>
                  <span>
                    Lien direct :{" "}
                    <a 
                      href="https://iptv-smarters-pro.en.uptodown.com/android" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      IPTV Smarters Pro sur Uptodown
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-2 mt-4 p-4 bg-muted/50 rounded-lg">
                  <span className="text-primary">💡</span>
                  <span className="text-sm">
                    Si vous connaissez déjà une application IPTV qui fonctionne bien sur votre appareil, vous pouvez l'utiliser.
                  </span>
                </li>
              </ul>
            </div>

            {/* Étape 3 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Play className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">3️⃣ Lancer l'application</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Ouvre <strong>IPTV Smarters Pro</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Sélectionne « <strong>Mobile</strong> ».</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Accepte les conditions générales.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Clique sur « <strong>Login With Xtream API</strong> ».</span>
                </li>
              </ul>
            </div>

            {/* Étape 4 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Key className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">4️⃣ Entrer les identifiants IPTV</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Nom du profil</strong> : choisis un nom (ex. Mon IPTV).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Nom d'utilisateur</strong> : celui fourni par RoiDuStreaming.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>Mot de passe</strong> : celui fourni par RoiDuStreaming.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span><strong>URL du serveur</strong> : celle fournie par RoiDuStreaming.</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-foreground font-medium">
                  ✔️ Vérifie les infos puis appuie sur « <strong>ADD USER</strong> ».
                </p>
              </div>
            </div>

            {/* Étape 5 */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Settings className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">5️⃣ Paramétrer le format du flux</h2>
              </div>
              <ul className="space-y-3 text-foreground/90">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Clique sur l'icône <strong>Paramètres</strong> (en haut à droite).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Va dans « <strong>Stream Format</strong> ».</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Choisis <strong>MPEGTS (.ts)</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Sauvegarde avec « <strong>SAVE CHANGES</strong> ».</span>
                </li>
              </ul>
            </div>

            {/* Étape 6 - Dépannage */}
            <div className="card-premium p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">6️⃣ Conseils & Dépannage</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-foreground/90 mb-4">
                  👉 <strong>Si le premier lien ne fonctionne pas</strong>, essaye l'un de ceux-ci :
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  {alternativeLinks.map((link, index) => (
                    <code key={index} className="block text-sm text-primary font-mono">
                      {index + 1}. {link}
                    </code>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-foreground/90">
                  👉 <strong>Si les films ou séries ne chargent pas</strong> :
                </p>
                <ul className="mt-2 space-y-2 text-foreground/80">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Relance l'application.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Si le problème persiste, teste un autre lien parmi ceux-ci.</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </section>

        {/* Pricing Section */}
        <TutorialPricingSection />

        <Footer />
        
        {/* Bouton Telegram flottant */}
        <a
          href="https://t.me/RoiDuStreaming"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-5 right-5 px-4 py-3 rounded-full flex items-center justify-center gap-2 z-50 transition-all duration-300 hover:scale-105 shadow-lg"
          style={{ backgroundColor: '#0088cc' }}
        >
          <span className="text-white font-medium text-sm whitespace-nowrap">Vous avez des questions ?</span>
          <Send className="w-5 h-5 text-white" />
        </a>
      </div>
    </>
  );
};

export default IptvAndroid;
